<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'

interface NavItem {
  id: string
  label: string
  icon: string
  to: string
  visible?: boolean
}

interface SsoAppItem {
  name: string
  description: string
  icon: string
  color: string
  color_hex: string | null
  url: string
}

const props = withDefaults(defineProps<{
  icon: string
  title: string
  navItems?: NavItem[]
  userMenuItems?: DropdownMenuItem[][]
  ssoAppsUrl?: string
  ssoUrl?: string
  avatarUrl?: string
  userName?: string
  userInitial?: string
  showDevColorPicker?: boolean
}>(), {
  ssoAppsUrl: '/api/sso/apps',
  showDevColorPicker: false,
  navItems: undefined,
  userMenuItems: undefined,
  ssoUrl: undefined,
  avatarUrl: undefined,
  userName: undefined,
  userInitial: undefined,
})

defineEmits<{
  logout: []
}>()

const route = useRoute()

function isActive(item: NavItem) {
  // Gère les sous-routes : /subscriptions matche /subscriptions/1 mais pas /subscriptions/renewals si c'est un item séparé
  const navPaths = (props.navItems || []).map(n => n.to)
  // Si un autre item est un sous-chemin plus spécifique, on ne matche pas le parent
  if (route.path === item.to) return true
  if (route.path.startsWith(item.to + '/')) {
    // Vérifier qu'un item plus spécifique ne matche pas
    const moreSpecific = navPaths.find(p => p !== item.to && p.startsWith(item.to + '/') && route.path.startsWith(p))
    return !moreSpecific
  }
  return false
}

// Apps SSO
const ssoApps = ref<SsoAppItem[]>([])
const ssoAppsLoading = ref(false)

async function fetchSsoApps() {
  if (!props.ssoAppsUrl) return
  ssoAppsLoading.value = true
  try {
    /*
     * Signature déclarée plutôt qu'inférée : l'adresse vient d'une prop, donc
     * d'une chaîne quelconque. Nitro la cherche alors parmi les routes de
     * l'application hôte, déroule leur type de retour pour y répondre, et
     * abandonne avant d'avoir conclu — l'appel perdait du même coup le type
     * que ce générique lui donnait.
     */
    const chargerApps = $fetch as unknown as (
      url: string,
    ) => Promise<{ data: SsoAppItem[] }>

    const res = await chargerApps(props.ssoAppsUrl)
    ssoApps.value = res.data
  }
  catch {
    ssoApps.value = []
  }
  finally {
    ssoAppsLoading.value = false
  }
}

function isCurrentApp(app: SsoAppItem): boolean {
  if (!import.meta.client) return false
  try {
    return new URL(app.url).origin === window.location.origin
  }
  catch {
    return false
  }
}

const visibleNavItems = computed(() => (props.navItems || []).filter(item => item.visible !== false))
</script>

<template>
  <header class="sticky top-0 z-50 bg-[var(--ui-primary)] text-white">
    <UContainer class="flex items-center justify-between h-14">
      <!-- Logo + Nom -->
      <NuxtLink to="/" class="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0 mr-3">
        <UIcon :name="icon" class="text-2xl text-white shrink-0" />
        <span class="font-bold text-lg text-white truncate hidden sm:inline">{{ title }}</span>
      </NuxtLink>

      <!-- Navigation droite -->
      <div class="flex items-center gap-1 min-w-0">
        <!-- Icônes de nav : scrollable en mobile -->
        <div v-if="visibleNavItems.length" class="flex items-center gap-1 overflow-x-auto scrollbar-hide">
          <UTooltip v-for="item in visibleNavItems" :key="item.id" :text="item.label">
            <UButton
              variant="ghost"
              color="neutral"
              :icon="item.icon"
              class="text-white hover:bg-white/15 shrink-0"
              :class="isActive(item) ? 'bg-white/10' : ''"
              :to="item.to"
            />
          </UTooltip>
        </div>

        <!-- Slot pour items de nav supplémentaires (paramètres, admin, etc.) -->
        <slot name="nav-extra" />

        <!-- DevColorPicker -->
        <slot v-if="showDevColorPicker" name="color-picker">
          <DevColorPicker />
        </slot>

        <!-- Séparateur avant apps (masqué si rien à gauche) -->
        <span v-if="ssoAppsUrl && (visibleNavItems.length || showDevColorPicker)" class="w-px h-5 bg-white/20 mx-1 shrink-0" />

        <!-- Menu applications SSO -->
        <UPopover v-if="ssoAppsUrl" :content="{ align: 'end' }" @update:open="(open: boolean) => { if (open) fetchSsoApps() }">
          <UButton
            variant="ghost"
            color="neutral"
            class="gap-2 text-white hover:bg-white/15 shrink-0"
          >
            <UIcon name="i-lucide-app-window" />
            <span class="hidden sm:inline">Applications</span>
            <UIcon name="i-lucide-chevron-down" class="text-xs" />
          </UButton>
          <template #content>
            <div class="p-1 min-w-48">
              <template v-if="ssoAppsLoading">
                <div class="flex items-center gap-2 px-2.5 py-1.5 text-sm text-[var(--ui-text-muted)]">
                  <UIcon name="i-lucide-loader-circle" class="animate-spin" />
                  Chargement...
                </div>
              </template>
              <template v-else-if="!ssoApps.length">
                <div class="flex items-center gap-2 px-2.5 py-1.5 text-sm text-[var(--ui-text-muted)]">
                  <UIcon name="i-lucide-inbox" />
                  Aucune application
                </div>
              </template>
              <template v-else>
                <component
                  :is="isCurrentApp(app) ? 'div' : 'a'"
                  v-for="app in ssoApps"
                  :key="app.url"
                  :href="isCurrentApp(app) ? undefined : app.url"
                  class="app-menu-item flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors"
                  :class="isCurrentApp(app) ? 'opacity-50 cursor-default' : 'cursor-pointer'"
                  :style="app.color_hex && !isCurrentApp(app) ? { '--app-color': app.color_hex } : {}"
                >
                  <UIcon
                    :name="app.icon || 'i-lucide-app-window'"
                    class="app-menu-icon text-base shrink-0 text-[var(--ui-text-muted)] transition-colors"
                  />
                  <span class="truncate">{{ app.name }}</span>
                </component>
              </template>
              <div v-if="ssoUrl" class="border-t border-[var(--ui-border)] mt-1 pt-1">
                <a
                  :href="ssoUrl"
                  class="app-menu-item flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-[var(--ui-text-muted)] transition-colors"
                >
                  <UIcon name="i-lucide-layout-grid" class="text-base shrink-0" />
                  <span>Toutes les applications</span>
                </a>
              </div>
            </div>
          </template>
        </UPopover>

        <!-- User menu -->
        <UDropdownMenu v-if="userMenuItems?.length" :items="userMenuItems" :content="{ side: 'bottom', align: 'end' }" :ui="{ content: 'min-w-48' }">
          <UButton
            variant="ghost"
            color="neutral"
            class="gap-2 text-white hover:bg-white/15 max-w-48 shrink-0"
          >
            <UAvatar
              :src="avatarUrl"
              :text="userInitial || '?'"
              size="xs"
            />
            <span v-if="userName" class="hidden sm:inline text-white text-sm truncate">{{ userName }}</span>
            <UIcon name="i-lucide-chevron-down" class="text-xs text-white/70 shrink-0" />
          </UButton>
        </UDropdownMenu>
      </div>
    </UContainer>
  </header>
</template>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Hover coloré pour les items du menu applications */
.app-menu-item:hover {
  background-color: color-mix(in srgb, var(--app-color, var(--ui-bg-elevated)) 10%, transparent);
}
.app-menu-item:hover .app-menu-icon {
  color: var(--app-color, var(--ui-text-muted));
}
</style>
